import { Controller } from "@hotwired/stimulus"

// Rotating headline text animation (typing effect)
// Cycles through words in .cd-words-wrapper, showing one at a time with letter-by-letter animation
export default class extends Controller {
  static values = {
    animationDelay: { type: Number, default: 1000 },
    lettersDelay: { type: Number, default: 100 },
    typeLettersDelay: { type: Number, default: 100 },
    selectionDuration: { type: Number, default: 250 }
  }

  connect() {
    this.headline = this.element.querySelector('.cd-headline')
    if (!this.headline) return

    this.wrapper = this.headline.querySelector('.cd-words-wrapper')
    if (!this.wrapper) return

    this.initHeadline()
  }

  disconnect() {
    // Clear any pending timeouts when controller disconnects
    if (this.animationTimeout) clearTimeout(this.animationTimeout)
  }

  initHeadline() {
    // Wrap each letter in <i> elements for the animation
    if (this.headline.classList.contains('letters')) {
      this.wrapper.querySelectorAll('span').forEach(word => {
        this.wrapLetters(word)
      })
    }

    // Start the animation cycle
    const visibleWord = this.wrapper.querySelector('.is-visible')
    if (visibleWord) {
      this.animationTimeout = setTimeout(() => this.hideWord(visibleWord), this.animationDelayValue)
    }
  }

  wrapLetters(word) {
    const text = word.textContent
    const isVisible = word.classList.contains('is-visible')

    // Clear existing content safely
    while (word.firstChild) {
      word.removeChild(word.firstChild)
    }

    // Create <i> element for each letter using safe DOM methods
    for (const letter of text) {
      const i = document.createElement('i')
      i.textContent = letter
      if (isVisible) {
        i.classList.add('in')
      }
      word.appendChild(i)
    }

    word.style.opacity = '1'
  }

  hideWord(word) {
    const nextWord = this.getNextWord(word)

    if (this.headline.classList.contains('type')) {
      // Typing effect: select, then hide letters
      this.wrapper.classList.add('selected')
      this.wrapper.classList.remove('waiting')

      setTimeout(() => {
        this.wrapper.classList.remove('selected')
        word.classList.remove('is-visible')
        word.classList.add('is-hidden')
        word.querySelectorAll('i').forEach(i => {
          i.classList.remove('in')
          i.classList.add('out')
        })
      }, this.selectionDurationValue)

      setTimeout(() => this.showWord(nextWord), this.selectionDurationValue)

    } else if (this.headline.classList.contains('letters')) {
      // Letter-by-letter effect
      const currentLetters = word.querySelectorAll('i')
      const nextLetters = nextWord.querySelectorAll('i')
      const hideFirst = currentLetters.length >= nextLetters.length

      this.hideLetter(currentLetters[0], word, hideFirst)
      this.showLetter(nextLetters[0], nextWord, hideFirst)

    } else {
      // Simple switch
      this.switchWord(word, nextWord)
      this.animationTimeout = setTimeout(() => this.hideWord(nextWord), this.animationDelayValue)
    }
  }

  showWord(word) {
    if (this.headline.classList.contains('type')) {
      const letters = word.querySelectorAll('i')
      if (letters.length > 0) {
        this.showLetter(letters[0], word, false)
      }
      word.classList.add('is-visible')
      word.classList.remove('is-hidden')
    }
  }

  hideLetter(letter, word, triggerHideWord) {
    if (!letter) return

    letter.classList.remove('in')
    letter.classList.add('out')

    const nextLetter = letter.nextElementSibling
    if (nextLetter) {
      setTimeout(() => this.hideLetter(nextLetter, word, triggerHideWord), this.lettersDelayValue)
    } else if (triggerHideWord) {
      this.animationTimeout = setTimeout(() => this.hideWord(this.getNextWord(word)), this.animationDelayValue)
    }
  }

  showLetter(letter, word, skipHideWord) {
    if (!letter) return

    letter.classList.add('in')
    letter.classList.remove('out')

    const nextLetter = letter.nextElementSibling
    if (nextLetter) {
      setTimeout(() => this.showLetter(nextLetter, word, skipHideWord), this.lettersDelayValue)
    } else {
      if (this.headline.classList.contains('type')) {
        setTimeout(() => this.wrapper.classList.add('waiting'), 200)
      }
      if (!skipHideWord) {
        this.animationTimeout = setTimeout(() => this.hideWord(word), this.animationDelayValue)
      }
    }
  }

  getNextWord(word) {
    const next = word.nextElementSibling
    if (next && next.tagName === 'SPAN') {
      return next
    }
    // Wrap around to first word
    return word.parentElement.querySelector('span')
  }

  switchWord(oldWord, newWord) {
    oldWord.classList.remove('is-visible')
    oldWord.classList.add('is-hidden')
    newWord.classList.remove('is-hidden')
    newWord.classList.add('is-visible')
  }
}
